<div class="p-6 bg-gray-50 min-h-screen">
  <div class="flex items-center justify-between mb-6">
    <div>
      <h1 class="text-2xl font-semibold text-gray-900">Drivers & Assignments</h1>
      <p class="text-sm text-gray-600">Monitor drivers and their current job status.</p>
    </div>
    <button
      type="button"
      class="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md shadow hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
      (click)="refresh()"
      [disabled]="loading"
    >
      <i class="fas fa-sync-alt mr-2"></i>
      {{ loading ? 'Refreshing...' : 'Refresh' }}
    </button>
  </div>

  <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
    <div class="stat-card">
      <p class="text-sm text-gray-500">Total Drivers</p>
      <p class="text-2xl font-semibold text-gray-900">{{ stats.total }}</p>
    </div>
    <div class="stat-card">
      <p class="text-sm text-gray-500">Assigned</p>
      <p class="text-2xl font-semibold text-blue-700">{{ stats.assigned }}</p>
    </div>
    <div class="stat-card">
      <p class="text-sm text-gray-500">Unassigned</p>
      <p class="text-2xl font-semibold text-gray-700">{{ stats.unassigned }}</p>
    </div>
  </div>

  <div class="bg-white shadow rounded-lg p-4 mb-4">
    <div class="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-3 md:space-y-0">
      <div class="flex-1">
        <label class="block text-sm font-medium text-gray-700 mb-1">Search</label>
        <input
          type="text"
          placeholder="Search by name, email, or phone"
          class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm"
          [(ngModel)]="searchTerm"
          (input)="applyFilters()"
        />
      </div>
      <div class="w-full md:w-48">
        <label class="block text-sm font-medium text-gray-700 mb-1">Assignment</label>
        <select
          class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm"
          [(ngModel)]="assignmentFilter"
          (change)="applyFilters()"
        >
          <option value="">All</option>
          <option value="assigned">Assigned</option>
          <option value="unassigned">Unassigned</option>
        </select>
      </div>
      <div class="flex items-end">
        <button
          type="button"
          class="px-3 py-2 text-sm text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          (click)="clearFilters()"
        >
          Clear Filters
        </button>
      </div>
    </div>
  </div>

  <div *ngIf="error" class="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
    {{ error }}
  </div>

  <div class="bg-white shadow rounded-lg overflow-hidden">
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Driver</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Job</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Status</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200" *ngIf="!loading && filteredDrivers.length">
          <tr *ngFor="let driver of filteredDrivers">
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm font-medium text-gray-900">{{ driver.fullName }}</div>
              <div class="text-xs text-gray-500">ID: {{ driver.id }}</div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
              <div>{{ driver.email }}</div>
              <div class="text-xs text-gray-500" *ngIf="driver.phoneNumber">Phone: {{ driver.phoneNumber }}</div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                [ngClass]="driver.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'"
              >
                <span class="w-2 h-2 mr-2 rounded-full" [ngClass]="driver.isActive ? 'bg-green-500' : 'bg-gray-400'"></span>
                {{ driver.isActive ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
              <div *ngIf="driver.currentJob; else noJob">
                <div class="font-semibold text-gray-900">Job #{{ driver.currentJob.id }}</div>
                <div class="text-xs text-gray-500" *ngIf="driver.currentJob.serviceType">Service: {{ driver.currentJob.serviceType }}</div>
                <div class="text-xs text-gray-500" *ngIf="driver.currentJob.pickupLocation">Pickup: {{ driver.currentJob.pickupLocation }}</div>
                <div class="text-xs text-gray-500" *ngIf="driver.currentJob.dropoffLocation">Dropoff: {{ driver.currentJob.dropoffLocation }}</div>
              </div>
              <ng-template #noJob>
                <span class="text-xs text-gray-500 italic">No active assignment</span>
              </ng-template>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span
                *ngIf="driver.currentJob; else unassigned"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                [ngClass]="getJobBadgeClass(driver.currentJob.status)"
              >
                {{ driver.currentJob.status || 'N/A' }}
              </span>
              <ng-template #unassigned>
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                  Unassigned
                </span>
              </ng-template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div *ngIf="loading" class="p-6 text-center text-gray-500">Loading drivers...</div>
    <div *ngIf="!loading && !filteredDrivers.length" class="p-6 text-center text-gray-500">No drivers found.</div>
  </div>
</div>

